"use client";
import React, { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Link } from "react-scroll";
import { FaGithub, FaLinkedin, FaInstagramSquare } from "react-icons/fa";

/* ─────────────────────────────────────────
   VARIANTS
───────────────────────────────────────── */
const fadeUp = {
  hidden: { y: 30, opacity: 0 },
  show: (d = 0) => ({
    y: 0,
    opacity: 1,
    transition: { duration: 0.7, delay: d, ease: [0.22, 1, 0.36, 1] },
  }),
};

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08, delayChildren: 0.05 } },
};

const itemVariant = {
  hidden: { y: 20, opacity: 0 },
  show: {
    y: 0,
    opacity: 1,
    transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
  },
};

/* ─────────────────────────────────────────
   FOOTER LINK
───────────────────────────────────────── */
function FooterNavLink({ to, label }) {
  return (
    <motion.div variants={itemVariant}>
      <Link
        to={to}
        spy
        smooth
        duration={600}
        offset={-80}
        className="group flex items-center gap-1.5 cursor-pointer"
      >
        <span className="w-0 group-hover:w-3 h-px bg-[#00f0ff] transition-all duration-300 flex-shrink-0" />
        <span
          className="text-xs tracking-[0.18em] uppercase text-white/35
            group-hover:text-white/70 transition-colors duration-300"
          style={{ fontFamily: "'DM Mono', monospace" }}
        >
          {label}
        </span>
      </Link>
    </motion.div>
  );
}

/* ─────────────────────────────────────────
   SOCIAL LINK
───────────────────────────────────────── */
function SocialLink({ href, Icon, label }) {
  return (
    <motion.a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      variants={itemVariant}
      whileHover={{ scale: 1.15, y: -3 }}
      whileTap={{ scale: 0.9 }}
      className="group w-10 h-10 rounded-full flex items-center justify-center
        border border-white/[0.07] bg-white/[0.02]
        hover:border-[#00f0ff]/40 hover:bg-[#00f0ff]/[0.07]
        transition-colors duration-300"
    >
      <Icon className="w-4 h-4 text-white/35 group-hover:text-[#00f0ff] transition-colors duration-300" />
    </motion.a>
  );
}

/* ─────────────────────────────────────────
   MAIN FOOTER
───────────────────────────────────────── */
const Footer = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const year = new Date().getFullYear();

  const navLinks = [
    { to: "home", label: "Home" },
    { to: "about", label: "About" },
    { to: "education", label: "Education" },
    { to: "experience", label: "Experience" },
    { to: "projects", label: "Projects" },
    { to: "contact", label: "Contact" },
  ];

  return (
    <footer
      ref={ref}
      className="relative overflow-hidden"
      style={{ background: "#030308" }}
    >
      {/* ── Grid bg ── */}
      <svg
        className="absolute inset-0 w-full h-full pointer-events-none opacity-[0.025]"
        aria-hidden
      >
        <defs>
          <pattern
            id="footer-grid"
            width="72"
            height="72"
            patternUnits="userSpaceOnUse"
          >
            <path
              d="M 72 0 L 0 0 0 72"
              fill="none"
              stroke="white"
              strokeWidth="0.4"
            />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#footer-grid)" />
      </svg>

      {/* Orb */}
      <div
        className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] pointer-events-none"
        aria-hidden
      >
        <div
          style={{
            background:
              "radial-gradient(ellipse, rgba(0,71,255,0.08) 0%, transparent 70%)",
            filter: "blur(60px)",
            width: "100%",
            height: "100%",
          }}
        />
      </div>

      {/* ── Top beam ── */}
      <motion.div
        initial={{ scaleX: 0 }}
        animate={inView ? { scaleX: 1 } : {}}
        transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
        className="absolute top-0 left-0 right-0 h-px origin-left pointer-events-none"
        style={{
          background:
            "linear-gradient(90deg, transparent, #0047ff, #00f0ff, transparent)",
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-10">
        {/* ── BIG CTA ── */}
        <div className="py-16 lg:py-24 flex flex-col lg:flex-row items-start lg:items-end justify-between gap-10 border-b border-white/[0.04]">
          {/* Left: Tagline */}
          <div className="flex-1">
            <motion.div
              custom={0}
              variants={fadeUp}
              initial="hidden"
              animate={inView ? "show" : "hidden"}
              className="flex items-center gap-3 mb-4"
            >
              <div className="h-px w-8 bg-gradient-to-r from-[#0047ff] to-[#00f0ff]" />
              <span
                className="text-[9px] tracking-[0.32em] uppercase text-[#00f0ff]/50"
                style={{ fontFamily: "'DM Mono', monospace" }}
              >
                Ready to ship?
              </span>
            </motion.div>

            <div className="overflow-hidden">
              <motion.p
                custom={0.1}
                variants={fadeUp}
                initial="hidden"
                animate={inView ? "show" : "hidden"}
                className="text-[clamp(2.4rem,6vw,5rem)] font-black leading-[0.9] tracking-tight text-white"
                style={{ fontFamily: "'Syne', sans-serif" }}
              >
                Start a project
              </motion.p>
            </div>
            <div className="overflow-hidden">
              <motion.p
                custom={0.18}
                variants={fadeUp}
                initial="hidden"
                animate={inView ? "show" : "hidden"}
                className="text-[clamp(2.4rem,6vw,5rem)] font-black leading-[0.9] tracking-tight"
                style={{
                  fontFamily: "'Syne', sans-serif",
                  WebkitTextStroke: "1.2px rgba(0,240,255,0.3)",
                  color: "transparent",
                }}
              >
                together.
              </motion.p>
            </div>
          </div>

          {/* Right: CTA button */}
          <motion.div
            custom={0.3}
            variants={fadeUp}
            initial="hidden"
            animate={inView ? "show" : "hidden"}
            className="flex-shrink-0"
          >
            <Link to="contact" spy smooth duration={600}>
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.96 }}
                className="relative group inline-flex items-center gap-3 px-8 py-4 rounded-full
                  font-semibold text-sm tracking-[0.18em] uppercase text-[#05050b] cursor-pointer overflow-hidden"
                style={{
                  background: "linear-gradient(135deg, #00f0ff, #0047ff)",
                  boxShadow: "0 0 35px rgba(0,240,255,0.22)",
                  fontFamily: "'DM Mono', monospace",
                }}
              >
                <span className="relative z-10">Get in touch</span>
                <motion.span
                  className="relative z-10"
                  animate={{ x: [0, 5, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                >
                  →
                </motion.span>
                <span
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-400"
                  style={{
                    background:
                      "linear-gradient(135deg, rgba(255,255,255,0.22), transparent 60%)",
                  }}
                />
              </motion.div>
            </Link>
          </motion.div>
        </div>

        {/* ── MIDDLE: Logo + Nav + Socials ── */}
        <div className="py-12 grid grid-cols-1 md:grid-cols-3 gap-10 border-b border-white/[0.04]">
          {/* Logo + tagline */}
          <motion.div
            custom={0.25}
            variants={fadeUp}
            initial="hidden"
            animate={inView ? "show" : "hidden"}
            className="flex flex-col gap-4"
          >
            <Link
              to="home"
              spy
              smooth
              duration={600}
              className="cursor-pointer self-start"
            >
              <span
                className="text-2xl font-black"
                style={{
                  fontFamily: "'Syne', sans-serif",
                  background:
                    "linear-gradient(135deg, #ffffff, rgba(255,255,255,0.5))",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                &lt;Bokor
                <span
                  style={{
                    background: "linear-gradient(135deg, #00f0ff, #0047ff)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  /&gt;
                </span>
              </span>
            </Link>

            <p
              className="text-sm text-white/30 leading-relaxed max-w-[220px]"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              Full Stack Engineer. Backend focused. Building systems that scale.
            </p>

            {/* Status dot */}
            <div className="flex items-center gap-2">
              <span className="relative flex h-1.5 w-1.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#00f0ff] opacity-60" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#00f0ff]" />
              </span>
              <span
                className="text-[9px] tracking-[0.2em] uppercase text-white/25"
                style={{ fontFamily: "'DM Mono', monospace" }}
              >
                Open to opportunities
              </span>
            </div>
          </motion.div>

          {/* Nav links */}
          <motion.div
            variants={stagger}
            initial="hidden"
            animate={inView ? "show" : "hidden"}
            className="flex flex-col gap-3"
          >
            <motion.span
              variants={itemVariant}
              className="text-[9px] tracking-[0.3em] uppercase text-white/20 mb-1"
              style={{ fontFamily: "'DM Mono', monospace" }}
            >
              Navigation
            </motion.span>
            {navLinks.map((link) => (
              <FooterNavLink key={link.to} {...link} />
            ))}
          </motion.div>

          {/* Contact + socials */}
          <motion.div
            variants={stagger}
            initial="hidden"
            animate={inView ? "show" : "hidden"}
            className="flex flex-col gap-4"
          >
            <motion.span
              variants={itemVariant}
              className="text-[9px] tracking-[0.3em] uppercase text-white/20"
              style={{ fontFamily: "'DM Mono', monospace" }}
            >
              Connect
            </motion.span>

            <motion.a
              variants={itemVariant}
              href="mailto:abubokor1066@gmail.com"
              className="group flex items-center gap-2 text-sm text-white/40
                hover:text-[#00f0ff]/80 transition-colors duration-300"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              <span className="text-[#00f0ff]/30 group-hover:text-[#00f0ff]/60 transition-colors text-xs">
                ◈
              </span>
              abubokor1066@gmail.com
            </motion.a>

            <motion.a
              variants={itemVariant}
              href="tel:+8801885236058"
              className="group flex items-center gap-2 text-sm text-white/40
                hover:text-[#00f0ff]/80 transition-colors duration-300"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              <span className="text-[#00f0ff]/30 group-hover:text-[#00f0ff]/60 transition-colors text-xs">
                ◉
              </span>
              +880 1885 236058
            </motion.a>

            <motion.div
              variants={itemVariant}
              className="flex items-center gap-2.5 pt-1"
            >
              <SocialLink
                href="https://github.com/AbuBokorprog"
                Icon={FaGithub}
                label="GitHub"
              />
              <SocialLink
                href="https://www.linkedin.com/in/md-abu-bokor/"
                Icon={FaLinkedin}
                label="LinkedIn"
              />
              <SocialLink href="#" Icon={FaInstagramSquare} label="Instagram" />
            </motion.div>
          </motion.div>
        </div>

        {/* ── BOTTOM BAR ── */}
        <motion.div
          custom={0.5}
          variants={fadeUp}
          initial="hidden"
          animate={inView ? "show" : "hidden"}
          className="py-6 flex flex-col sm:flex-row items-center justify-between gap-4"
        >
          <span
            className="text-[9px] tracking-[0.22em] uppercase text-white/18"
            style={{ fontFamily: "'DM Mono', monospace" }}
          >
            © {year} Abu Bokor. All rights reserved.
          </span>

          {/* Privacy policy */}
          {/* <div className="flex items-center gap-5">
            {["Privacy Policy", "Licensing"].map((item) => (
              <a
                key={item}
                href="#"
                className="text-[9px] tracking-[0.18em] uppercase text-white/18
                  hover:text-white/40 transition-colors duration-300"
                style={{ fontFamily: "'DM Mono', monospace" }}
              >
                {item}
              </a>
            ))}
          </div> */}

          <span
            className="text-[9px] tracking-[0.22em] uppercase text-white/12"
            style={{ fontFamily: "'DM Mono', monospace" }}
          >
            Bangladesh — {year}
          </span>
        </motion.div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800;900&family=DM+Mono:ital,wght@0,300;0,400;0,500;1,300&family=DM+Sans:wght@300;400;500&display=swap');
      `}</style>
    </footer>
  );
};

export default Footer;
